/**
 * <addon-stepper>
 *
 * Renders the +/- stepper UI for a single (non-grouped) upsell add-on
 * card. The new Figma design replaced the previous `<select>` quantity
 * dropdown with an inline stepper, but the existing
 * `<addons-quantity>` element already owns:
 *   • the cart submission (its hidden <select name="quantity">)
 *   • the bundle subtotal recalculation on `change`
 *
 * To avoid touching that flow we keep the <select> in the DOM (hidden)
 * and just mirror this stepper's value into it, dispatching `change` so
 * `addons-quantity#updateBundlePrice` runs as it always has.
 *
 * Respects `product.metafields.quantity.is_even` via `data-step` (set in
 * Liquid) so chair / bench addons only ever move in increments of 2.
 */
if (!customElements.get('addon-stepper')) {
  customElements.define(
    'addon-stepper',
    class AddonStepper extends HTMLElement {
      constructor() {
        super();

        this.step = parseInt(this.dataset.step, 10) || 1;
        this.maxQty = parseInt(this.dataset.maxQty, 10) || 20;

        this.decBtn = this.querySelector('[data-addon-decrement]');
        this.incBtn = this.querySelector('[data-addon-increment]');
        this.qtyDisplay = this.querySelector('[data-addon-qty]');
        this.select = this.querySelector('select[name="quantity"]');

        // Initial qty: whatever the hidden <select> currently holds
        // (typically 0 on page load).
        this.currentQty = this.select ? parseInt(this.select.value, 10) || 0 : 0;
        this.updateUI();

        this.bindEvents();
      }

      bindEvents() {
        if (this.decBtn) {
          this.decBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.changeQty(this.currentQty - this.step);
          });
        }
        if (this.incBtn) {
          this.incBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.changeQty(this.currentQty + this.step);
          });
        }
      }

      changeQty(newQty) {
        if (newQty < 0) newQty = 0;
        if (newQty > this.maxQty) newQty = this.maxQty;

        this.currentQty = this.clampToAvailableOption(newQty);
        this.updateUI();
        this.syncSelect();
      }

      // The hidden <select> may not list every integer (e.g. even-only
      // products skip odd values). Snap to the closest valid option ≤ qty.
      clampToAvailableOption(qty) {
        if (!this.select) return qty;
        const opts = Array.from(this.select.options).map((o) =>
          parseInt(o.value, 10)
        );
        if (opts.length === 0) return qty;
        if (opts.includes(qty)) return qty;
        let chosen = opts[0];
        opts.forEach((v) => {
          if (v <= qty && v > chosen) chosen = v;
        });
        return chosen;
      }

      syncSelect() {
        if (!this.select) return;
        const desired = String(this.currentQty);
        if (this.select.value !== desired) {
          this.select.value = desired;
          this.select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      updateUI() {
        if (this.qtyDisplay) {
          this.qtyDisplay.textContent = String(this.currentQty);
        }
        if (this.decBtn) {
          this.decBtn.toggleAttribute('disabled', this.currentQty <= 0);
        }
        if (this.incBtn) {
          this.incBtn.toggleAttribute('disabled', this.currentQty >= this.maxQty);
        }
      }
    }
  );
}
